import React from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import PageIcon from '@atlaskit/icon/glyph/page';
import Image from '@atlaskit/image';
import { Stack } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import {
	srcDark as TaskOneIllustrationDark,
	srcLight as TaskOneIllustrationLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/trash';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { CardList, CardListItem } from '../../../common/ui/card-list';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages';
import { getDeprecatedOnesTasks } from '../../../common/utils/tasks';
import {
	useDeprecatedHeartbeats,
	useDeprecatedGroups,
	useDeprecatedNotificationPolicies,
} from '../../../services/store/deprecated-counts';
import { useDeprecatedIntegrations } from '../../../services/store/integrations';
import { messages } from './messages';

export const DeprecatedOnesStep = () => {
	const { formatMessage } = useIntl();

	const [{ deprecatedIntegrations, hasDeprecatedIntegration }] = useDeprecatedIntegrations();
	const [{ groups, hasDeprecatedGroups }] = useDeprecatedGroups();
	const [{ heartbeats, hasDeprecatedGlobalHeartbeats }] = useDeprecatedHeartbeats();
	const [{ notificationPolicies, hasDeprecatedNotificationPolicies }] =
		useDeprecatedNotificationPolicies();
	const isSiteAdmin = useIsSiteAdmin();
	const isPrimaryAdmin = useIsAdmin();

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={<FormattedMessage {...stepMessages.deprecatedOnesTitleNonFinal} />}
				description={<FormattedMessage {...stepMessages.deprecatedOnesDescriptionNonFinal} />}
			/>

			{/* Task One */}
			<TransitionHomeContentBox
				illustration={
					<Image
						src={TaskOneIllustrationLight}
						srcDark={TaskOneIllustrationDark}
						alt=""
						width={130}
					/>
				}
			>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						{
							getDeprecatedOnesTasks({
								isAdmin: isSiteAdmin || isPrimaryAdmin,
								hasDeprecatedIntegration,
								hasDeprecatedGroups,
								hasDeprecatedGlobalHeartbeats,
								hasDeprecatedNotificationPolicies,
							})[0].description
						}
					</Heading>
					<CardList>
						{/* Integrations */}
						{hasDeprecatedIntegration && (
							<CardListItem
								title={formatMessage(messages.integrationsTitleNonFinal)}
								description={formatMessage(messages.integrationsDescriptionNonFinal, {
									deprecatedIntegrationCount: deprecatedIntegrations.data?.count,
								})}
								afterIcon={
									<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<PageIcon label="" size="small" />}
											aria-label={formatMessage(messages.integrationsTitleNonFinal)}
										/>
									</Tooltip>
								}
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Integrations"
							/>
						)}

						{/* Groups */}
						{hasDeprecatedGroups && (isSiteAdmin || isPrimaryAdmin) && (
							<CardListItem
								title={formatMessage(messages.groupsTitleNonFinal)}
								description={formatMessage(messages.groupsDescriptionNonFinal, {
									deprecatedGroupCount: groups.data?.count,
								})}
								afterIcon={
									<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<PageIcon label="" size="small" />}
											aria-label={formatMessage(messages.groupsTitleNonFinal)}
										/>
									</Tooltip>
								}
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Groups"
							/>
						)}

						{/* Heartbeats V1 */}
						{hasDeprecatedGlobalHeartbeats && (isSiteAdmin || isPrimaryAdmin) && (
							<CardListItem
								title={formatMessage(messages.heartbeatsTitleNonFinal)}
								description={formatMessage(messages.heartbeatsDescriptionNonFinal, {
									deprecatedGlobalHeartbeatCount: heartbeats.data?.globalHeartbeatCount,
								})}
								afterIcon={
									<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<PageIcon label="" size="small" />}
											aria-label={formatMessage(messages.heartbeatsTitleNonFinal)}
										/>
									</Tooltip>
								}
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Heartbeats-V1"
							/>
						)}

						{/* Teams API */}
						<CardListItem
							title={formatMessage(messages.teamsApiTitleNonFinal)}
							description={formatMessage(messages.teamsApiDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.teamsApiTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Teams-API"
						/>

						{/* Incident templates */}
						<CardListItem
							title={formatMessage(messages.incidentTemplatesTitleNonFinal)}
							description={formatMessage(messages.incidentTemplatesDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.incidentTemplatesTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Incident-templates"
						/>

						{/* Incident command center */}
						<CardListItem
							title={formatMessage(messages.iccAndBridgesTitleNonFinal)}
							description={formatMessage(messages.iccAndBridgesDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.iccAndBridgesTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Incident-Command-Center-and-Conference-Bridges"
						/>

						{/* Notification policies */}
						{hasDeprecatedNotificationPolicies && (isSiteAdmin || isPrimaryAdmin) && (
							<CardListItem
								title={formatMessage(messages.notificationPoliciesTitleNonFinal)}
								description={formatMessage(messages.notificationPoliciesDescriptionNonFinal, {
									deprecatedNotificationPolicyCount: notificationPolicies.data?.count,
								})}
								afterIcon={
									<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<PageIcon label="" size="small" />}
											aria-label={formatMessage(messages.notificationPoliciesTitleNonFinal)}
										/>
									</Tooltip>
								}
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Notification-policies-V1"
							/>
						)}

						{/* Actions and incident rules */}
						<CardListItem
							title={formatMessage(messages.actionsAndIncidentRulesTitleNonFinal)}
							description={formatMessage(messages.actionsAndIncidentRulesDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.actionsAndIncidentRulesTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Actions-and-incident-rules"
						/>

						{/* Reports */}
						<CardListItem
							title={formatMessage(messages.reportsTitleNonFinal)}
							description={formatMessage(messages.reportsDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.reportsTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Some-reports"
						/>

						{/* Opsgenie edge connector */}
						<CardListItem
							title={formatMessage(messages.oecTitleNonFinal)}
							description={formatMessage(messages.oecDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.oecTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#Opsgenie-Edge-Connector"
						/>

						{/* External services */}
						<CardListItem
							title={formatMessage(messages.servicesTitleNonFinal)}
							description={formatMessage(messages.servicesDescriptionNonFinal)}
							afterIcon={
								<Tooltip content={formatMessage(messages.tooltipTextNonFinal)}>
									<Button
										appearance="subtle"
										iconBefore={<PageIcon label="" size="small" />}
										aria-label={formatMessage(messages.servicesTitleNonFinal)}
									/>
								</Tooltip>
							}
							href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/#External-services"
						/>
					</CardList>
				</Stack>
			</TransitionHomeContentBox>
		</Stack>
	);
};
